/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import bbox from '@turf/bbox';
import { points, polygon as turfPolygon } from '@turf/helpers';
import { centerOfMass } from '@turf/center-of-mass';
import centerPoint from '@turf/center';
import {
  DEFAULT_ZOOM,
  DEFAULT_CENTER,
  GOOGLE_MAP_ID,
} from '@src/v3/constants/GoogleMaps';
import { GoogleMapComponentProps } from '@src/v3/interfaces/components/coreUI/googleMap';
import { apiPropertyItem } from '@src/v3/interfaces/api/publicProperties';
import { Property } from '@src/interfaces/app/propertyLists';
import { getPropertiesBulk } from '@src/v3/services/properties';
import { createRoot } from 'react-dom/client';
import { formatCurrency, splitAddress } from '@src/v3/utils/helper';
import debounce from 'lodash/debounce';
import { cloneDeep } from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import { APP_ROUTE } from '@src/v3/constants/routes';
import { useGoogleMaps } from '@src/v3/hooks/useGoogleMaps';
import MarkerPopupLoader from './markerPopupLoader';
import MarkerPopUp from './markerPopUp';
import ZoomControls from './zoomControls';
import Locations from './locations';

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  wrapperClassName = '',
  markers,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  height = 200,
  width = 0,
  onMapZoom = () => {
    return;
  },
  onMarkerClick,
  onMarkerSelect,
  useSliderPopup = false,
  onMapClick,
}) => {
  // hooks
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { isLoaded, isLoading, error } = useGoogleMaps();

  // Query variables
  const neighborhoodParam = searchParams?.get('neighborhood');
  const zipCodeParam = searchParams?.get('zip_code');
  const boundCoordinates = searchParams?.get('bound_coordinates');

  // states and refs
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  // Caching objects
  const customMarkersRef = useRef<{
    [key: string]: google.maps.marker.AdvancedMarkerElement;
  }>({});
  const markersOnScreenRef = useRef<{
    [key: string]: google.maps.marker.AdvancedMarkerElement;
  }>({});
  const openPopupsRef = useRef<{ [key: string]: google.maps.InfoWindow }>({});
  const openPopupRootsRef = useRef<{ [key: string]: any }>({});
  const polygonOverlaysRef = useRef<google.maps.Polygon[]>([]);
  const zoomChangedListenerRef = useRef<google.maps.MapsEventListener | null>(
    null
  );
  const dragendListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const zoomClosePopupsListenerRef =
    useRef<google.maps.MapsEventListener | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(
    null
  );
  const isDraggingRef = useRef<boolean>(false);
  const isInitialBoundsSet = useRef<boolean>(false);
  const locationRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const locationDivRef = useRef<HTMLDivElement | null>(null);
  const activeMarkerIdRef = useRef<string | null>(null);

  // 🔥 Re-render Locations whenever searchParams changes
  useEffect(() => {
    if (locationRootRef.current) {
      locationRootRef.current.render(<Locations searchParams={searchParams} />);
    }
  }, [searchParams]);

  const toggleMarkerHighlight = (
    marker: google.maps.marker.AdvancedMarkerElement,
    highlight: boolean
  ) => {
    const element = marker.content as HTMLElement;
    if (!element) return;

    const FOCUS_BLUE = '#0072F5';
    const DROP_SHADOW =
      '0 2px 4px -2px rgba(0,0,0,0.10), 0 4px 6px -1px rgba(0,0,0,0.10)';

    if (highlight) {
      element.classList.add('!z-[9999]');
      element.style.setProperty('background', FOCUS_BLUE, 'important');
      element.style.setProperty('color', 'white', 'important');
      element.style.setProperty('border', '1px solid white', 'important');
      element.style.setProperty(
        'box-shadow',
        `0 0 0 2px ${FOCUS_BLUE}, ${DROP_SHADOW}`,
        'important'
      );
    } else {
      element.classList.remove('!z-[9999]');
      element.style.removeProperty('background');
      element.style.removeProperty('color');
      element.style.removeProperty('border');
      element.style.removeProperty('box-shadow');
    }
  };

  /**
   * Helper function to update marker label for AdvancedMarkerElement
   */
  const updateMarkerLabel = (
    marker: google.maps.marker.AdvancedMarkerElement,
    text: string
  ) => {
    const element = marker.content as HTMLElement;
    if (element) {
      element.textContent = text;
    }
  };

  // Detect screen size for desktop vs mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 640); // Tailwind's sm breakpoint
    };

    // Check on mount
    checkScreenSize();

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  /**
   * Initialize the Google Map
   * and load all marker images of different states
   */
  useEffect(() => {
    const initializeMap = () => {
      const container = mapContainerRef.current;
      if (
        container &&
        isLoaded &&
        window.google &&
        window.google.maps &&
        window.google.maps.Map &&
        typeof window.google.maps.Map === 'function'
      ) {
        let mapCenter: { lat: number; lng: number };
        if (Array.isArray(center)) {
          mapCenter = { lat: center[1], lng: center[0] };
        } else if ('lon' in center) {
          mapCenter = { lat: center.lat, lng: center.lon } as {
            lat: number;
            lng: number;
          };
        } else {
          mapCenter = { lat: center.lat, lng: center.lng } as {
            lat: number;
            lng: number;
          };
        }

        const map = new google.maps.Map(container, {
          center: mapCenter,
          zoom,
          mapId: GOOGLE_MAP_ID,
          disableDefaultUI: true,
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          scrollwheel: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
        });
        // Only render ZoomControls and locations on desktop

        if (isDesktop) {
          // Create custom horizontal zoom controls
          const zoomControlDiv = document.createElement('div');
          const zoomControlRoot = createRoot(zoomControlDiv);
          zoomControlRoot.render(
            <ZoomControls
              onPlusClick={() => map.setZoom((map.getZoom() || 10) + 1)}
              onMinusClick={() => map.setZoom((map.getZoom() || 10) - 1)}
            />
          );

          // Add to map
          map.controls[google.maps.ControlPosition.TOP_RIGHT].push(
            zoomControlDiv
          );

          const locationDiv = document.createElement('div');
          const locationRoot = createRoot(locationDiv);
          locationDivRef.current = locationDiv;
          locationRootRef.current = locationRoot;
          locationRoot.render(<Locations searchParams={searchParams} />);

          map.controls[google.maps.ControlPosition.TOP_LEFT].push(locationDiv);
        }
        setMap(map);

        const debouncedZoomEndHandler = debounce(() => {
          // Don't trigger onMapZoom during initial bounds setting
          if (!isInitialBoundsSet.current) {
            return;
          }
          // // Don't trigger onMapZoom when a popup is open
          // if (Object.keys(openPopupsRef.current).length > 0) {
          //   return;
          // }
          const bounds = map.getBounds();
          if (bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            onMapZoom([sw.lat(), sw.lng(), ne.lat(), ne.lng()]);
          }
        }, 1000);

        map.addListener('zoom_changed', debouncedZoomEndHandler);
        map.addListener('dragend', debouncedZoomEndHandler);
        if (zoomChangedListenerRef.current) {
          google.maps.event.removeListener(
            zoomChangedListenerRef.current as any
          );
        }
        if (dragendListenerRef.current) {
          google.maps.event.removeListener(dragendListenerRef.current as any);
        }

        zoomChangedListenerRef.current = map.addListener(
          'zoom_changed',
          debouncedZoomEndHandler
        );
        dragendListenerRef.current = map.addListener(
          'dragend',
          debouncedZoomEndHandler
        );
      }
    };

    // Only initialize if Google Maps API is loaded and map doesn't exist yet
    if (!map && isLoaded) {
      initializeMap();
    }

    return () => {
      if (zoomChangedListenerRef.current) {
        google.maps.event.removeListener(zoomChangedListenerRef.current as any);
        zoomChangedListenerRef.current = null;
      }
      if (dragendListenerRef.current) {
        google.maps.event.removeListener(dragendListenerRef.current as any);
        dragendListenerRef.current = null;
      }
      if (zoomClosePopupsListenerRef.current) {
        google.maps.event.removeListener(
          zoomClosePopupsListenerRef.current as any
        );
        zoomClosePopupsListenerRef.current = null;
      }
      // Cleanup location components
      if (locationRootRef.current) {
        locationRootRef.current.unmount();
        locationRootRef.current = null;
      }
      if (locationDivRef.current) {
        locationDivRef.current = null;
      }
      polygonOverlaysRef.current.forEach((p) => p.setMap(null));
      polygonOverlaysRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isLoaded, isDesktop]);

  // Set up map click listener for desktop and mobile
  useEffect(() => {
    if (!map) return undefined;

    // Track drag state
    const dragStartListener = map.addListener('dragstart', () => {
      isDraggingRef.current = true;
    });

    const dragEndListener = map.addListener('dragend', () => {
      // Reset drag state after a short delay to allow click event to check it
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);
    });

    // Listen for clicks - only trigger callback if not dragging
    mapClickListenerRef.current = map.addListener('click', () => {
      if (!isDraggingRef.current) {
        if (activeMarkerIdRef.current) {
          const marker = customMarkersRef.current[activeMarkerIdRef.current];
          if (marker) toggleMarkerHighlight(marker, false);
          activeMarkerIdRef.current = null;
        }
        // This triggers API call with the visible map area
        const bounds = map.getBounds();
        if (bounds) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const boundsArray = [sw.lat(), sw.lng(), ne.lat(), ne.lng()];

          // Call onMapClick if provided (mobile), otherwise call onMapZoom (desktop)
          if (onMapClick) {
            onMapClick(boundsArray);
          } else if (onMapZoom) {
            onMapZoom(boundsArray);
          }
        } else {
          if (onMapClick) {
            onMapClick();
          }
        }
      }
    });

    return () => {
      if (mapClickListenerRef.current) {
        google.maps.event.removeListener(mapClickListenerRef.current as any);
        mapClickListenerRef.current = null;
      }
      google.maps.event.removeListener(dragStartListener);
      google.maps.event.removeListener(dragEndListener);
    };
  }, [map, useSliderPopup, onMapClick, onMapZoom, isDesktop]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const drawLinesOnMap = (map: google.maps.Map) => {
    try {
      // Clear existing polygon overlays before drawing new ones
      polygonOverlaysRef.current.forEach((p) => p.setMap(null));
      polygonOverlaysRef.current = [];

      const polygons = markers?.features.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (feature: any) => feature.geometry.type === 'Polygon'
      );

      const pointsWithCoordinates = markers?.features
        .filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (feature: any) =>
            feature.geometry.type === 'Point' &&
            'display' in feature.properties &&
            !feature.properties.display
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((polygon: any) => polygon?.geometry?.coordinates);

      // Draw polygons
      polygons?.forEach((polygon: any) => {
        const paths = polygon.geometry.coordinates[0].map(
          ([lng, lat]: [number, number]) => ({
            lat,
            lng,
          })
        );

        const poly = new google.maps.Polygon({
          paths,
          map,
          fillColor: 'transparent',
          fillOpacity: 0,
          strokeColor: '#E01BFF',
          strokeOpacity: 1,
          strokeWeight: 3,
        });
        polygonOverlaysRef.current.push(poly);
      });

      if (polygons?.length > 0 || pointsWithCoordinates?.length > 0) {
        const featuresCollection =
          polygons?.map((polygon: any) => ({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: polygon.geometry.coordinates,
            },
          })) || [];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const geojson: any = {
          type: 'FeatureCollection',
          features: featuresCollection,
        };

        if (pointsWithCoordinates && pointsWithCoordinates.length > 0) {
          const features = points(pointsWithCoordinates);
          const centerPointOfResult = centerPoint(features);
          geojson.features.push(centerPointOfResult);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bboxInstance: any = bbox(geojson);
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(new google.maps.LatLng(bboxInstance[1], bboxInstance[0]));
        bounds.extend(new google.maps.LatLng(bboxInstance[3], bboxInstance[2]));
        map.fitBounds(bounds);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getLatLngWithCenter = (
    features: any,
    move: any,
    map: google.maps.Map
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const featuresLatLng: any = [];
    let moveLatLng = [];

    if (features && features.length > 0) {
      features
        .filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (feature: any) => feature.geometry.type === 'Polygon'
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((polygon: any) => {
          const polygonPoint = turfPolygon(polygon?.geometry?.coordinates);
          const center = centerOfMass(polygonPoint);
          featuresLatLng.push(center?.geometry?.coordinates);
        });
    }

    if (move && move.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      moveLatLng = move.map((item: any) => {
        if (
          item &&
          typeof item.lng === 'number' &&
          typeof item.lat === 'number'
        ) {
          return [item.lat, item.lng];
        }
        const coordinates = Object.values(item);
        coordinates?.pop();
        return coordinates as number[];
      });
    }

    const coordinates = [...moveLatLng, ...(featuresLatLng || [])];
    const bounds = new google.maps.LatLngBounds();

    coordinates.forEach((coord) => {
      bounds.extend({ lat: coord[1], lng: coord[0] });
    });

    map.fitBounds(bounds);

    const maxZoom = neighborhoodParam || zipCodeParam ? 12 : 20;
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      const currentZoom = map.getZoom();
      if (typeof currentZoom === 'number' && currentZoom > maxZoom) {
        map.setZoom(maxZoom);
      }
    });
  };

  /**
   * On change of markers selectedMarkerId
   * ensure that map reflects the current state
   */
  useEffect(() => {
    if (map && markers != null) {
      const { move } = markers;

      const cloneOfMarkers = cloneDeep(markers);
      cloneOfMarkers.features = cloneOfMarkers?.features?.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (feature: any) =>
          feature.geometry.type === 'Point' &&
          !('display' in feature.properties)
      );

      // if pathName is agent public profile, then center map on the first property
      if (
        !boundCoordinates &&
        pathName?.substring(0, pathName?.lastIndexOf('/')) ===
          APP_ROUTE?.AGENT_PUBLIC_PROFILE?.substring(
            0,
            APP_ROUTE?.AGENT_PUBLIC_PROFILE?.lastIndexOf('/')
          ) &&
        markers?.features?.length > 0
      ) {
        const [firstProperty] = markers?.features;
        const [lng, lat] = firstProperty?.geometry?.coordinates || [];

        if (lng && lat) map?.panTo({ lat, lng });
      }

      if (move && move.length > 0) {
        getLatLngWithCenter(markers?.features, move, map);
      }

      if (move && move.length === 0) drawLinesOnMap(map);

      const newMarkers: {
        [key: string]: google.maps.marker.AdvancedMarkerElement;
      } = {};
      const propertyMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

      // Create standard markers for clustering with caching
      cloneOfMarkers.features?.forEach((feature: any) => {
        if (feature.geometry.type === 'Point') {
          const [lng, lat] = feature.geometry.coordinates;
          const position = new google.maps.LatLng(lat, lng);
          const id = feature.properties?.ids;

          // Check if marker already exists in cache
          let marker = customMarkersRef.current[id];

          if (!marker) {
            // Create marker with custom label only if not cached
            const markerElement = document.createElement('div');
            if (feature.properties?.units > 1) {
              markerElement.textContent = `${feature.properties?.units} units`;
              markerElement.className = 'multi-marker-label';
            } else if (feature.properties?.max_price) {
              markerElement.textContent = formatCurrency(
                feature.properties.max_price,
                true
              );
              markerElement.className = 'marker-label';
            }
            // Create custom HTML element for marker label

            marker = new google.maps.marker.AdvancedMarkerElement({
              map,
              position,
              content: markerElement,
              zIndex: 2000,
            });

            // Store feature data for popups
            (marker as any).featureData = feature;
            (marker as any).featureId = id;

            // Cache the marker
            customMarkersRef.current[id] = marker;

            // Add click handler only for new markers
            marker.addListener('click', async () => {
              const currentActiveId = activeMarkerIdRef.current;

              // Deactivate previous marker if exists
              if (currentActiveId && currentActiveId !== id) {
                const prevMarker = customMarkersRef.current[currentActiveId];
                if (prevMarker) {
                  toggleMarkerHighlight(prevMarker, false);
                }
              }

              // Activate current marker
              toggleMarkerHighlight(marker, true);
              activeMarkerIdRef.current = id;

              // Call onMarkerClick callback if provided
              if (onMarkerClick) {
                const [lng, lat] = feature.geometry.coordinates;
                onMarkerClick({ lat, lng });
              }

              const ids = id.split(',');
              let markerText = '$0';
              if (feature.properties?.units > 1) {
                markerText = `${feature.properties?.units} units`;
              } else if (feature.properties?.max_price) {
                markerText = formatCurrency(feature.properties.max_price, true);
              }

              if (openPopupsRef.current[id]) {
                openPopupsRef.current[id].close();
                delete openPopupsRef.current[id];
                // Remove active class when closing popup
                updateMarkerLabel(marker, markerText);
                toggleMarkerHighlight(marker, false);
                activeMarkerIdRef.current = null;
                return;
              }

              // Close other popups and remove their active states
              for (const markerId in openPopupsRef.current) {
                if (openPopupsRef.current.hasOwnProperty(markerId)) {
                  openPopupsRef.current[markerId].close();
                  delete openPopupsRef.current[markerId];
                  // Unmount any React root associated with this popup
                  if (openPopupRootsRef.current[markerId]) {
                    openPopupRootsRef.current[markerId].unmount();
                    delete openPopupRootsRef.current[markerId];
                  }

                  // Reset other active markers using cache
                  const otherMarker = markersOnScreenRef.current[markerId];
                  if (otherMarker) {
                    const otherFeature = (otherMarker as any).featureData;
                    let otherMarkerText = '$0';
                    if (otherFeature.properties?.units > 1) {
                      otherMarkerText = `${otherFeature.properties?.units} units`;
                    } else if (otherFeature.properties?.max_price) {
                      otherMarkerText = formatCurrency(
                        otherFeature.properties.max_price,
                        true
                      );
                    }

                    updateMarkerLabel(otherMarker, otherMarkerText);
                  }
                }
              }

              // Set current marker as active (pink)
              updateMarkerLabel(marker, markerText);

              // If using slider popup (mobile), fetch data and call onMarkerSelect
              if (useSliderPopup && onMarkerSelect) {
                try {
                  const response = await getPropertiesBulk(ids);
                  const propertyData = response.map((res: apiPropertyItem) => ({
                    id: res.id,
                    image: res?.image_information?.images[0]?.image || '',
                    address: splitAddress(
                      res.general_information.address,
                      res.general_information.is_exclusive === false
                    ).title,
                    locationDetails: splitAddress(
                      res.general_information.address,
                      res.general_information.is_exclusive === false
                    ).subTitle,
                    bedrooms: res.general_information.bedrooms,
                    bathrooms: res.general_information.bathrooms,
                    squareFootage: res.general_information.square_footage,
                    cost: res.economic_information?.cost || 0,
                    netEffectiveRent:
                      res.economic_information?.net_effective_rent,
                  }));
                  onMarkerSelect(propertyData);
                } catch (error) {
                  console.error('Error fetching property data:', error);
                }

                if (!isDesktop) {
                  // Calculate offset position to show marker slightly above center
                  const bounds = map.getBounds();
                  if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    // Calculate offset as a percentage of the viewport height (approximately 15% upward)
                    const latOffset = (ne.lat() - sw.lat()) * 0.1;
                    const offsetPosition = new google.maps.LatLng(
                      position.lat() - latOffset,
                      position.lng()
                    );
                    map.panTo(offsetPosition);
                  } else {
                    map.panTo(position);
                  }
                }
                return;
              }

              // Show loading popup with better styling
              const loadingContainer = document.createElement('div');
              loadingContainer.style.padding = '10px';
              loadingContainer.style.textAlign = 'center';
              loadingContainer.style.fontSize = '14px';
              loadingContainer.style.color = '#666';
              const loadingRoot = createRoot(loadingContainer);
              loadingRoot.render(<MarkerPopupLoader />);

              const loadingPopup = new google.maps.InfoWindow({
                content: loadingContainer,
                position: position,
                pixelOffset: new google.maps.Size(0, -42),
                disableAutoPan: true, // Prevent map from shifting
                headerDisabled: true,
              });

              loadingPopup.open(map);

              try {
                const response = await getPropertiesBulk(ids);
                const container = document.createElement('div');
                container.className = wrapperClassName;
                const root = createRoot(container);
                root.render(
                  <div className={response?.length === 1 ? 'p-0' : 'p-4'}>
                    {response?.map((res: apiPropertyItem, index: number) => (
                      <Fragment key={index}>
                        <MarkerPopUp
                          property={res as unknown as Property}
                          image={
                            res?.image_information?.images[0]?.thumbnail ?? ''
                          }
                          address={
                            splitAddress(
                              res.general_information.address,
                              res.general_information.is_exclusive === false
                            ).title
                          }
                          locationDetails={
                            splitAddress(
                              res.general_information.address,
                              res.general_information.is_exclusive === false
                            ).subTitle
                          }
                          bedrooms={res.general_information.bedrooms}
                          bathrooms={res.general_information.bathrooms}
                          squareFootage={res.general_information.square_footage}
                          cost={res.economic_information?.cost}
                          totalProperties={response.length}
                          netEffectiveRent={
                            res.economic_information?.net_effective_rent
                          }
                        />
                      </Fragment>
                    ))}
                  </div>
                );

                const propertyPopup = new google.maps.InfoWindow({
                  content: container,
                  position: position,
                  pixelOffset: new google.maps.Size(0, -42), // Offset popup slightly above marker
                  maxWidth: 400, // Consistent popup width
                  disableAutoPan: false,
                  headerDisabled: true, // Remove the close button
                });

                loadingPopup.close();
                loadingRoot.unmount();

                // Add smooth opening animation
                setTimeout(() => {
                  propertyPopup.open(map);
                  openPopupsRef.current[id] = propertyPopup;
                  openPopupRootsRef.current[id] = root;
                }, 100); // Small delay for smoother transition

                // Close popup when clicking outside (on map)
                const mapClickListener = map.addListener('click', () => {
                  if (openPopupsRef.current[id]) {
                    propertyPopup.close();
                    delete openPopupsRef.current[id];
                    if (openPopupRootsRef.current[id]) {
                      openPopupRootsRef.current[id].unmount();
                      delete openPopupRootsRef.current[id];
                    }

                    // Remove active class when popup closes using cached marker
                    const markerItem = markersOnScreenRef.current[id];
                    if (markerItem) {
                      const feature = (markerItem as any).featureData;
                      let markerText = '$0';
                      if (feature.properties?.units > 1) {
                        markerText = `${feature.properties?.units} units`;
                      } else if (feature.properties?.max_price) {
                        markerText = formatCurrency(
                          feature.properties.max_price,
                          true
                        );
                      }

                      updateMarkerLabel(markerItem, markerText);
                      toggleMarkerHighlight(markerItem, false);
                      if (activeMarkerIdRef.current === id) {
                        activeMarkerIdRef.current = null;
                      }
                    }

                    google.maps.event.removeListener(mapClickListener);
                  }
                });
              } catch (error) {
                console.error('Error fetching property:', error);
                loadingPopup.close();
                try {
                  // In case render already happened
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  loadingRoot && loadingRoot.unmount();
                } catch (e) {
                  // noop
                }
              }
            });
          }

          // Update marker position if it has changed
          marker.position = position;

          // Add to new markers tracking and property markers array
          newMarkers[id] = marker;
          propertyMarkers.push(marker);
        }
      });

      // Remove markers that are no longer needed
      for (const id in markersOnScreenRef.current) {
        if (!newMarkers[id]) {
          markersOnScreenRef.current[id].map = null;
          delete markersOnScreenRef.current[id];
        }
      }

      // Update markers on screen reference
      markersOnScreenRef.current = newMarkers;

      if (propertyMarkers.length > 0) {
        if (clustererRef.current) {
          // Smooth update: clear and immediately add new markers
          clustererRef.current.clearMarkers();
          clustererRef.current.addMarkers(propertyMarkers);
        } else {
          // Create new clusterer only if none exists
          const clusterer = new MarkerClusterer({
            markers: propertyMarkers,
            map,
            algorithm: new SuperClusterAlgorithm({
              radius: 85, // Standard clustering radius
              maxZoom: 14, // Don't cluster beyond this zoom
              minPoints: 2, // Minimum points to form a cluster
              extent: 256,
            }),
            onClusterClick: () => {
              // Disable default cluster click behavior
              return false;
            },
            renderer: {
              render: ({ count, position }) => {
                // Use simpler marker creation for faster rendering
                // Create cluster marker element
                const clusterElement = document.createElement('div');
                const scale = (() => {
                  if (count >= 100) return 25;
                  if (count >= 10) return 22;
                  return 20;
                })();
                const fontSize = (() => {
                  if (count >= 100) return '13px';
                  if (count >= 10) return '12px';
                  return '14px';
                })();
                clusterElement.className = 'cluster-label';
                clusterElement.style.cssText = `
                  width: ${scale * 2}px;
                  height: ${scale * 2}px;
                  font-size: ${fontSize};
                `;
                clusterElement.textContent = count.toString();

                const clusterMarker =
                  new google.maps.marker.AdvancedMarkerElement({
                    map,
                    position,
                    content: clusterElement,
                    zIndex: 1200, // Lower than markers
                  });

                // Add cluster click handler
                clusterMarker.addListener('click', () => {
                  const currentZoom = map.getZoom() || 10;
                  const newZoom = Math.min(currentZoom + 3, 15); // Smaller increment and max zoom 15
                  map.setCenter(position);
                  map.setZoom(newZoom);
                });

                return clusterMarker;
              },
            },
          });

          clustererRef.current = clusterer;
        }

        // Only close popups on zoom changes, not on drag
        const debouncedClosePopups = debounce(() => {
          for (const markerId in openPopupsRef.current) {
            if (openPopupsRef.current.hasOwnProperty(markerId)) {
              openPopupsRef.current[markerId].close();
              delete openPopupsRef.current[markerId];

              // Remove active class when popup closes on zoom using cached marker
              const markerItem = markersOnScreenRef.current[markerId];
              if (markerItem) {
                const feature = (markerItem as any).featureData;
                let markerText = '$0';
                if (feature.properties?.units > 1) {
                  markerText = `${feature.properties?.units} units`;
                } else if (feature.properties?.max_price) {
                  markerText = formatCurrency(
                    feature.properties.max_price,
                    true
                  );
                }

                updateMarkerLabel(markerItem, markerText);
              }
            }
          }
        }, 100);

        // Only close on zoom changes, not on bounds_changed (which includes drag)
        if (zoomClosePopupsListenerRef.current) {
          google.maps.event.removeListener(
            zoomClosePopupsListenerRef.current as any
          );
        }
        zoomClosePopupsListenerRef.current = map.addListener(
          'zoom_changed',
          debouncedClosePopups
        );
      }
    }

    if (map && !markers) {
      // Handle empty markers case - clear map
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markers]);

  useEffect(() => {
    if (map) {
      if (boundCoordinates) {
        // Split and parse coordinates
        const [sw_lat, sw_lng, ne_lat, ne_lng] = boundCoordinates
          ?.split(',')
          .map(Number);

        if ([sw_lat, sw_lng, ne_lat, ne_lng].every((v) => Number.isFinite(v))) {
          const bounds = new google.maps.LatLngBounds();

          // Southwest: (lat1, lng1) -> (sw_lat, sw_lng)
          // Northeast: (lat2, lng2) -> (ne_lat, ne_lng)
          const sw = new google.maps.LatLng(sw_lat, sw_lng);
          const ne = new google.maps.LatLng(ne_lat, ne_lng);

          bounds.extend(sw);
          bounds.extend(ne);

          // Use smooth animation like Mapbox
          map.fitBounds(bounds, {
            top: 10,
            right: 10,
            bottom: 10,
            left: 10,
          });

          // Set flag after initial bounds are applied to allow future zoom events
          setTimeout(() => {
            isInitialBoundsSet.current = true;
          }, 1500);
        }
      } else {
        // If no bound coordinates, set flag immediately to allow normal map interactions
        setTimeout(() => {
          isInitialBoundsSet.current = true;
        }, 1000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Handle loading and error states
  if (error) {
    return (
      <div className="w-full sticky sm:top-[121px]">
        <div
          className="w-full rounded-xl flex items-center justify-center bg-gray-100 text-red-600"
          style={{
            height: `${height}px`,
            width: width ? `${width}px` : '100%',
            minHeight: '200px',
          }}
        >
          <div className="text-center">
            <p className="text-sm font-medium">Failed to load Google Maps</p>
            <p className="text-xs text-gray-500 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full sticky sm:top-[121px]">
        <div
          className="w-full rounded-xl flex items-center justify-center bg-gray-100 text-gray-600"
          style={{
            height: `${height}px`,
            width: width ? `${width}px` : '100%',
            minHeight: '200px',
          }}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm font-medium mt-2">Loading Google Maps...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full sticky sm:top-[121px]">
      <div
        ref={mapContainerRef}
        className="w-full rounded-xl"
        style={{
          height: `${height}px`,
          width: width ? `${width}px` : '100%',
          minHeight: '200px', // Ensure minimum height for map visibility
        }}
      />
    </div>
  );
};

export default GoogleMapComponent;
